/**
 * Storage and file upload related constants
 */

export interface FileTypeInfo {
  mimeTypes: readonly string[];
  extensions: readonly string[];
}

export type FileType = (typeof FILE_TYPE)[keyof typeof FILE_TYPE];

export const STORAGE_BUCKET = import.meta.env.VITE_SUPABASE_STORAGE_BUCKET;
export const MULTIPART_UPLOAD_THRESHOLD = 100 * 1024 * 1024; // 100MB threshold for multipart
export const MAX_FILE_SIZE = 2000 * 1024 * 1024; // 2GB in bytes

export class StorageConstants {
  /** Longer text renders in one <pre> slowly enough to freeze the tab. */
  static readonly MAX_TEXT_PREVIEW_CHARACTERS = 5 * 1024 * 1024;
}

export const SUPPORTED_FILE_TYPES = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/tiff": [".tiff"],
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
  "application/vnd.ms-powerpoint": [".ppt"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
  "application/rtf": [".rtf"],
  "text/plain": [".txt"],
  "text/csv": [".csv"],
  "text/tab-separated-values": [".tsv"],
  "message/rfc822": [".eml"],
} as const;

export const FILE_TYPE = {
  PDF: "pdf",
  WORD: "word",
  SPREADSHEET: "spreadsheet",
  IMAGE: "image",
  TEXT: "text",
} as const;

function buildFileTypeInfo(
  mimeTypes: readonly (keyof typeof SUPPORTED_FILE_TYPES)[]
): FileTypeInfo {
  return { mimeTypes, extensions: mimeTypes.flatMap((mime) => SUPPORTED_FILE_TYPES[mime]) };
}

export const FILE_TYPE_INFO: Record<FileType, FileTypeInfo> = {
  [FILE_TYPE.PDF]: buildFileTypeInfo(["application/pdf"]),
  [FILE_TYPE.WORD]: buildFileTypeInfo([
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
  ]),
  [FILE_TYPE.SPREADSHEET]: buildFileTypeInfo([
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
    "text/tab-separated-values",
  ]),
  [FILE_TYPE.IMAGE]: buildFileTypeInfo(["image/png", "image/jpeg", "image/tiff"]),
  [FILE_TYPE.TEXT]: buildFileTypeInfo(["text/plain", "message/rfc822"]),
};
